import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PricingPlans } from '@/components/PricingPlans';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import { segmentOrder, segmentLabels } from '@/config/segments';
import {
  Package, Clock, Percent, BarChart3, ArrowRight, ChevronDown, TrendingUp,
  Croissant, ShoppingBasket, Truck, UtensilsCrossed,
} from 'lucide-react';

const whoItsFor = [
  { slug: 'bakers', icon: Croissant, text: 'Home & small-batch bakers' },
  { slug: 'market-stalls', icon: ShoppingBasket, text: 'Market stall sellers' },
  { slug: 'food-trucks', icon: Truck, text: 'Food trucks' },
  { slug: 'caterers', icon: UtensilsCrossed, text: 'Caterers' },
];

const features = [
  {
    icon: Package,
    title: 'Ingredient Costing',
    text: 'Enter pack prices once. CostCraft breaks every ingredient down to a per-gram cost and re-prices every recipe the moment a supplier price changes.',
  },
  {
    icon: Clock,
    title: 'Labour & Time Costing',
    text: 'Add prep, bake and finishing time with your hourly rate. See exactly what your labour is costing you on every batch — not just ingredients.',
  },
  {
    icon: Percent,
    title: 'Overhead Costing',
    text: 'Fold rent, power and insurance into a simple overhead percentage so your true cost per unit reflects the real cost of running your bakery.',
  },
  {
    icon: BarChart3,
    title: 'Wholesale Pricing & Analytics',
    text: 'Set wholesale prices with confidence and track profit margins, cost trends and recipe comparisons to protect your bottom line.',
  },
];

const faqs = [
  {
    q: 'Do I need a credit card to start?',
    a: 'No. The Starter plan is free and needs no credit card. Sign up, add your ingredients and start costing recipes straight away. You only enter payment details if you choose to upgrade to a paid plan.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Paid plans are month-to-month (or yearly if you prefer) with no lock-in contract. You can cancel or change your plan at any time from Settings.',
  },
  {
    q: 'What currency are your prices in?',
    a: 'All pricing is in Australian Dollars (AUD). Home Baker is $7.99/month, Pro Baker is $19/month and Bakery Business is $29/month, with roughly 17% off if you pay yearly. Existing founding customers keep their current price while their subscription remains active.',
  },
  {
    q: 'What happens if I hit my ingredient or recipe limit?',
    a: "Nothing is deleted. Your existing ingredients and recipes stay exactly as they are — you're simply prompted to upgrade if you want to add more. Downgrade later and your data is still there.",
  },
  {
    q: 'What can I do on the free Starter plan?',
    a: 'The free Starter plan includes ingredient costing for up to 15 ingredients and 3 recipes. You can calculate batch cost, unit cost, retail price and profit margin. Labour, overheads, scaling, exports, wholesale pricing and advanced analytics are available on paid plans.',
  },
];

const FaqItem = ({ faq, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#DBEAFE]" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-medium text-[#111827] text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {faq.q}
        </span>
        <ChevronDown className={`w-5 h-5 text-[#2C5F7C] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}>
        <p className="text-[#6B7280] text-sm sm:text-base leading-relaxed pr-8">{faq.a}</p>
      </div>
    </div>
  );
};

export const LandingPage = () => {
  const navigate = useNavigate();

  const scrollToPricing = () => {
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FC]" data-testid="landing-page">
      {/* Top Nav */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#DBEAFE]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-9 h-9 rounded-md object-contain" />
            <span className="text-xl sm:text-2xl font-bold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              CostCraft
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={scrollToPricing}
              className="hidden sm:inline text-sm font-medium text-[#6B7280] hover:text-[#2C5F7C] transition-colors"
              data-testid="nav-pricing-link"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="text-sm font-medium text-[#2C5F7C] hover:underline"
              data-testid="nav-signin-btn"
            >
              Sign In
            </button>
            <Button
              onClick={() => navigate('/auth?mode=signup')}
              className="rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white text-sm px-4 sm:px-5"
              data-testid="nav-start-free-btn"
            >
              Start Free
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#EFF6FF] text-[#2C5F7C] text-xs font-medium px-3 py-1 mb-6">
              <TrendingUp className="w-3.5 h-3.5" />
              Recipe costing for bakers & small food makers
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Stop losing margin<br />without knowing it.
            </h1>
            <p className="text-base sm:text-lg text-[#6B7280] leading-relaxed mb-8 max-w-xl">
              Ingredient prices change. One small increase can quietly wipe out your profit.
              CostCraft updates every recipe instantly, so you always know your real numbers —
              cost, margin and the right price to charge.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate('/auth?mode=signup')}
                className="rounded-full bg-[#2C5F7C] hover:bg-[#1E4A60] text-white h-12 px-8 text-base"
                data-testid="hero-start-free-btn"
              >
                Start Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={scrollToPricing}
                variant="outline"
                className="rounded-full border-[#2C5F7C] text-[#2C5F7C] hover:bg-[#EFF6FF] h-12 px-8 text-base"
                data-testid="hero-pricing-btn"
              >
                See pricing
              </Button>
            </div>
            <p className="text-sm text-[#9CA3AF] mt-4">No credit card required · Free Starter plan · Prices in AUD</p>
            <p className="text-sm text-[#6B7280] mt-2 font-medium" data-testid="hero-trust-line">
              Made in Australia · GST-aware · Cancel anytime
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#2C5F7C]/15 to-[#9B7EBD]/15 rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl shadow-2xl bg-white p-2 border border-[#E5E7EB]">
              <img
                src="/hero-dashboard.jpg"
                alt="CostCraft dashboard showing a profit margin alert"
                className="rounded-2xl w-full object-cover aspect-[11/5]"
                data-testid="hero-product-screenshot"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20" data-testid="who-its-for-section">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#111827] mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Who it's for
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {whoItsFor.map(({ slug, icon: Icon, text }) => (
            <Link
              key={slug}
              to={`/for/${slug}`}
              className="bg-white rounded-2xl border border-[#DBEAFE] p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`who-its-for-card-${slug}`}
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#EFF6FF] text-[#2C5F7C] flex items-center justify-center mb-3">
                <Icon className="w-6 h-6" />
              </div>
              <div className="font-semibold text-[#111827]">{text}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20" data-testid="how-it-works-section">
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-2xl px-6 sm:px-10 py-8 sm:py-10">
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-10">
            {[
              { step: '1', text: 'Add your ingredient prices' },
              { step: '2', text: 'Build your recipes with time & overheads' },
              { step: '3', text: 'Get alerted when a price rise eats your margin' },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3" data-testid={`how-it-works-step-${item.step}`}>
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2C5F7C] text-white text-sm font-bold flex items-center justify-center">
                  {item.step}
                </span>
                <span className="text-[#374151] text-sm sm:text-base pt-1">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Everything that goes into a price
          </h2>
          <p className="text-[#6B7280] text-base sm:text-lg">
            Real cost isn't just ingredients. CostCraft accounts for the full picture so you price for profit.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#DBEAFE] p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#2C5F7C] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-[#111827] mb-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {f.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <TestimonialsSection />

      {/* Pricing */}
      <section id="pricing" className="bg-white border-y border-[#DBEAFE] scroll-mt-16" data-testid="landing-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <PricingPlans publicMode />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="landing-faq">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Frequently asked questions
        </h2>
        <div>
          {faqs.map((faq, i) => (
            <FaqItem key={i} faq={faq} index={i} />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#2C5F7C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Know your real numbers today
          </h2>
          <p className="text-white/85 text-base sm:text-lg mb-8">
            Start free in minutes. Upgrade only when you're ready.
          </p>
          <Button
            onClick={() => navigate('/auth?mode=signup')}
            className="rounded-full bg-white text-[#2C5F7C] hover:bg-[#EFF6FF] h-12 px-8 text-base"
            data-testid="footer-start-free-btn"
          >
            Start Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F8F9FC] border-t border-[#DBEAFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B7280]">
          <div className="flex items-center gap-2">
            <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-7 h-7 rounded-md object-contain" />
            <span className="font-semibold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>CostCraft</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            {segmentOrder.map((s) => (
              <Link key={s} to={`/for/${s}`} className="hover:text-[#2C5F7C]" data-testid={`footer-segment-link-${s}`}>
                {segmentLabels[s]}
              </Link>
            ))}
            <button onClick={() => navigate('/terms')} className="hover:text-[#2C5F7C]" data-testid="footer-terms-link">Terms & Privacy</button>
            <a href="mailto:hello@costcraft.app" className="hover:text-[#2C5F7C]">Contact</a>
          </div>
          <span className="text-[#9CA3AF]">© 2026 CostCraft. Prices in AUD.</span>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 text-center text-xs text-[#9CA3AF]" data-testid="footer-credit">
          Designed by Bellarine Tech Assist
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
